import { apiSlice } from "../baseQuery";
import { Row } from "../type";

export interface SendbirdUser {
  user_id: string;
  nickname: string;
  profile_url?: string;
  is_active?: boolean;
  is_online?: boolean;
  metadata?: Record<string, any>;
}

export interface UpdateSendbirdUserRequest {
  nickname?: string;
  profile_url?: string;
  metadata?: Record<string, any>;
}

// Sendbird 토큰 관련 인터페이스
export interface SendbirdTokenResponse {
  sendbird_token: string;
  expires_at?: number; // 만료 시간 (밀리초)
  expires_in?: number; // 만료까지 남은 시간 (초)
}

// 읽지 않은 메시지 수 인터페이스
export interface UnreadCountResponse {
  unread_count: number;
}

// 채팅 가용성 인터페이스
export interface ChatAvailabilityResponse {
  isAvailable: boolean;
}

const sendbirdApi = apiSlice
  .enhanceEndpoints({
    addTagTypes: [
      "SendbirdUser",
      "SendbirdToken",
      "SendbirdUnreadCount",
      "ChatAvailability",
    ],
  })
  .injectEndpoints({
    endpoints: (builder) => ({
      // 사용자 조회
      getSendbirdUser: builder.query<SendbirdUser, string>({
        query: (userId) => {
          return {
            url: `/v1/sendbird/users/${userId}`,
            method: "GET",
          };
        },
        transformResponse: (response: Row<SendbirdUser>) => {
          return response.row;
        },
        providesTags: (_result, _error, userId) => [
          { type: "SendbirdUser", id: userId },
        ],
      }),

      // 사용자 업데이트
      updateSendbirdUser: builder.mutation<
        SendbirdUser,
        { userId: string; updates: UpdateSendbirdUserRequest }
      >({
        query: ({ userId, updates }) => {
          return {
            url: `/v1/sendbird/users/${userId}`,
            method: "PUT",
            body: updates,
          };
        },
        transformResponse: (response: Row<SendbirdUser>) => {
          return response.row;
        },
        invalidatesTags: (_result, _error, { userId }) => [
          { type: "SendbirdUser", id: userId },
        ],
      }),

      // Sendbird 토큰 발급/갱신
      getSendbirdToken: builder.mutation<SendbirdTokenResponse, void>({
        query: () => {
          return {
            url: `/v1/chat/sendbird-token`,
            method: "POST",
          };
        },
        transformResponse: (response: SendbirdTokenResponse) => {
          return response;
        },
        transformErrorResponse: (response: any) => {
          return response;
        },
        invalidatesTags: ["SendbirdToken"],
      }),

      // 읽지 않은 메시지 수 조회
      getUnreadCount: builder.query<UnreadCountResponse, void>({
        query: () => {
          return {
            url: `/v1/chat/unread-count`,
            method: "GET",
          };
        },
        transformResponse: (response: UnreadCountResponse) => {
          return response;
        },
        providesTags: ["SendbirdUnreadCount"],
      }),

      // 채팅 가용성 조회
      getChatAvailability: builder.query<ChatAvailabilityResponse, void>({
        query: () => {
          return {
            url: `/v1/chat/availability`,
            method: "GET",
          };
        },
        transformResponse: (response: ChatAvailabilityResponse) => {
          return response;
        },
        providesTags: ["ChatAvailability"],
      }),
    }),
    overrideExisting: false,
  });

export const {
  useGetSendbirdUserQuery,
  useLazyGetSendbirdUserQuery,
  useUpdateSendbirdUserMutation,
  useGetSendbirdTokenMutation,
  useGetUnreadCountQuery,
  useLazyGetUnreadCountQuery,
  useGetChatAvailabilityQuery,
} = sendbirdApi;

// API export for internal use
export { sendbirdApi };

// 사용자 초기화 커스텀 훅
export const useInitSendbirdUser = () => {
  const [getSendbirdUser] = useLazyGetSendbirdUserQuery();
  const [updateSendbirdUser] = useUpdateSendbirdUserMutation();

  const initializeUser = async (
    userId: string,
    nickname: string,
    profileUrl?: string,
    metadata?: Record<string, any>,
  ): Promise<SendbirdUser | null> => {
    if (!userId || typeof userId !== "string") {
      return null;
    }

    try {
      // 1. 사용자 조회 시도
      const getUserResult = await getSendbirdUser(userId);

      // RTK Query lazy queries return the data directly
      const userData = getUserResult.data;

      if (userData) {
        // 정보가 변경되었으면 업데이트
        const existingUser = userData;

        if (
          existingUser.nickname !== nickname ||
          existingUser.profile_url !== profileUrl ||
          JSON.stringify(existingUser.metadata) !== JSON.stringify(metadata)
        ) {
          const updateResult = await updateSendbirdUser({
            userId,
            updates: { nickname, profile_url: profileUrl, metadata },
          });
          return "data" in updateResult && updateResult.data
            ? updateResult.data
            : existingUser;
        }

        return existingUser;
      }

      // 2. 404 에러인 경우 사용자가 없음을 로그
      if (getUserResult.error) {
        if (
          "status" in getUserResult.error &&
          getUserResult.error.status === 404
        ) {
          // User not found (404)
          return null;
        }
      }

      return null;
    } catch (error) {
      // Treat user initialization failure as non-critical - continue without user profile
      return null;
    }
  };

  return { initializeUser };
};

// 캐시 관련 유틸리티
const USER_CACHE_KEY = "sendbird_user_cache";
const CACHE_DURATION = 1000 * 60 * 60; // 1시간

export const getCachedUser = (userId: string): SendbirdUser | null => {
  try {
    const cached = localStorage.getItem(`${USER_CACHE_KEY}_${userId}`);
    if (!cached) return null;

    const { user, timestamp } = JSON.parse(cached);

    if (Date.now() - timestamp > CACHE_DURATION) {
      localStorage.removeItem(`${USER_CACHE_KEY}_${userId}`);
      return null;
    }

    return user;
  } catch (error) {
    // Treat localStorage failure as a cache miss - not critical
    return null;
  }
};

export const setCachedUser = (userId: string, user: SendbirdUser): void => {
  try {
    localStorage.setItem(
      `${USER_CACHE_KEY}_${userId}`,
      JSON.stringify({
        user,
        timestamp: Date.now(),
      }),
    );
  } catch (error) {
    // Non-critical failure: ignore localStorage write errors
  }
};

// 매니저 export
export { default as tokenManager } from "./tokenManager";
